import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, TrendingUp, Package, BarChart3, Shield, ArrowRight, Star, Users, Zap, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PLANS = [
  {
    name: 'Starter',
    price: 29,
    description: 'Perfect for part-time sellers getting started',
    features: ['Up to 50 active listings', 'Basic analytics dashboard', 'Standard support', 'Community access'],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Pro',
    price: 79,
    description: 'For serious dealers growing their business',
    features: ['Up to 200 active listings', 'Advanced analytics & reports', 'Priority support', 'Verified Dealer badge', 'Reduced commission (5%)', 'Featured in Dealer directory'],
    cta: 'Go Pro',
    highlight: true,
  },
  {
    name: 'Elite',
    price: 149,
    description: 'For established dealers and shops',
    features: ['Unlimited active listings', 'Full analytics suite', 'Dedicated account manager', 'Verified Dealer badge', 'Lowest commission (3%)', 'Featured placement', 'Custom storefront page', 'Priority listing visibility'],
    cta: 'Go Elite',
    highlight: false,
  },
];

const BENEFITS = [
  { icon: BadgeCheck, title: 'Verified Dealer Badge', description: 'Stand out with a trust badge that tells buyers you are a vetted, professional dealer.' },
  { icon: TrendingUp, title: 'Reduced Commissions', description: 'Lower platform fees mean more profit per sale. Pro dealers save 2%, Elite saves 4%.' },
  { icon: BarChart3, title: 'Advanced Analytics', description: 'Track your sales, views, conversion rates, and identify your best-performing items.' },
  { icon: Package, title: 'More Listings', description: 'Scale your inventory without limits. List hundreds or thousands of items.' },
  { icon: Star, title: 'Featured Placement', description: 'Get your listings in front of more buyers with priority positioning in search results.' },
  { icon: Shield, title: 'Dedicated Support', description: 'Elite dealers get a dedicated account manager for dispute resolution and platform help.' },
];

export default function DealerProgramPage() {
  return (
    <div className="min-h-screen bg-surface-base">
      <Header />

      {/* Hero */}
      <section className="py-20 bg-surface-card border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-6">
            <BadgeCheck className="w-4 h-4 text-gold" />
            <span className="text-sm text-gold font-medium">Dealer Program</span>
          </div>
          <h1 className="font-clash text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Grow Your <span className="gold-text">Dealer Business</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10">
            Whether you're a part-time seller or a full-time shop, our Dealer Program gives you the tools, 
            visibility, and reduced fees to scale your business on CoinHustle.
          </p>
          <Link to="/register" className="btn-gold px-8 py-4 inline-flex items-center gap-2 uppercase tracking-wider text-sm">
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">Pricing</p>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white">Choose Your Plan</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map(plan => (
              <div key={plan.name} className={`p-8 border ${plan.highlight ? 'bg-gold/5 border-gold' : 'bg-surface-card border-border-subtle'} relative`}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold text-black text-xs font-bold uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <h3 className="font-clash text-2xl font-semibold text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="font-clash text-4xl font-bold text-gold">${plan.price}</span>
                  <span className="text-zinc-500">/month</span>
                </div>
                <p className="text-sm text-zinc-400 mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className={`block text-center py-3 font-semibold text-sm uppercase tracking-wider ${plan.highlight ? 'btn-gold' : 'btn-secondary'}`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-zinc-500 mt-8">
            Dealer subscriptions launching soon. Register now to be notified when enrollment opens.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-surface-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white">Dealer Benefits</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map(b => (
              <div key={b.title} className="p-6 bg-surface-base border border-border-subtle hover:border-gold/30 transition-colors">
                <b.icon className="w-8 h-8 text-gold mb-4" />
                <h3 className="font-clash text-lg font-medium text-white mb-2">{b.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
